import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, User, Building, Hash, Edit2, Save, X, FileText, Lock } from 'lucide-react';

const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [loadingData, setLoadingData] = useState(true);

  // Permission state
  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);

  // Get auth token from localStorage or sessionStorage
  const getAuthToken = () => {
    return localStorage.getItem('session_token') ||
           localStorage.getItem('authToken') ||
           sessionStorage.getItem('authToken');
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      'Accept': 'application/json'
    };
  };

  // Check permissions from localStorage (fast + robust)
  useEffect(() => {
    const checkPermissions = () => {
      setPermissionLoading(true);
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (!userDataStr) {
          setHasEditPermission(false);
          setStatusMessage({ text: 'Please login again.', type: 'error' });
          setPermissionLoading(false);
          return;
        }

        const userData = JSON.parse(userDataStr);
        let canEdit = false;

        // Profile permission can be present in different categories based on backend
        if (userData.permission?.profile && Array.isArray(userData.permission.profile)) {
          canEdit = userData.permission.profile.includes('profile');
        }
        if (!canEdit && userData.permission?.company && Array.isArray(userData.permission.company)) {
          canEdit = userData.permission.company.includes('profile');
        }
        if (!canEdit && userData.permission?.master && Array.isArray(userData.permission.master)) {
          canEdit = userData.permission.master.includes('profile');
        }

        setHasEditPermission(canEdit);

        if (!canEdit) {
          setStatusMessage({
            text: 'Access Denied: You do not have permission to edit company settings.',
            type: 'error'
          });
        } else {
          setStatusMessage({ text: '', type: '' });
        }
      } catch (error) {
        setStatusMessage({
          text: 'Error checking permissions. Please refresh the page.',
          type: 'error'
        });
      } finally {
        setPermissionLoading(false);
      }
    };

    checkPermissions();
  }, []);

  // Load profile data (view-only allowed even without permission)
  useEffect(() => {
    const token = getAuthToken();
    const baseUrl = 'https://newerp.awasthivitality.com/api/company/showsettings';
    const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;

    fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
      .then(async (res) => {
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) {
          const text = await res.text();
          throw new Error(`Unexpected response type: ${ct} ${text?.slice(0, 120)}`);
        }
        return res.json();
      })
      .then(data => {
        if (data.success) {
          const mappedData = {
            profile_id: data.data.profile_id,
            hospitalName: data.data.hospital_name,
            profilePicture: data.data.logo_url,
            contactPersonName: data.data.contact_per_name,
            mobileNumber: data.data.mobile,
            whatsappNumber: data.data.whatsapp,
            telephone: data.data.telephone,
            emailId: data.data.email,
            address: data.data.address,
            city: data.data.city,
            state: data.data.state,
            pincode: data.data.pincode,
            termsAndConditions: data.data.terms,
          };
          setProfileData(mappedData);
        } else {
          setStatusMessage({ text: data.message || 'Failed to load profile.', type: 'error' });
        }
        setLoadingData(false);
      })
      .catch(err => {
        console.error('Error fetching profile data:', err);
        setStatusMessage({ text: 'Failed to load profile data.', type: 'error' });
        setLoadingData(false);
      });
  }, []);

  useEffect(() => {
    if (isEditing && Object.keys(formData).length === 0 && profileData) {
      setFormData({ ...profileData });
    }
  }, [isEditing, profileData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    // Block if permission unknown or missing
    if (permissionLoading) {
      setStatusMessage({ text: 'Please wait, checking permissions...', type: 'error' });
      return;
    }
    if (!hasEditPermission) {
      setStatusMessage({
        text: 'Access Denied: You do not have permission to edit company settings.',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    setStatusMessage({ text: '', type: '' });

    try {
      const token = getAuthToken();
      if (!token) {
        setStatusMessage({ text: 'No valid token found. Please log in again.', type: 'error' });
        setIsLoading(false);
        return;
      }

      // Optional: cross-check via permission API if you want parity with your snippet
      // const permUrl = `https://newerp.awasthivitality.com/api/user/permissions?token=${encodeURIComponent(token)}`;
      // const permRes = await fetch(permUrl, { headers: getAuthHeaders() });
      // const permData = await permRes.json();
      // You can also inspect permData.data?.profile/company/master if needed

      // Build multipart form and send everything (including logo) to the expected endpoint
      const fd = new FormData();
      // Backend expects field name 'logo'
      if (formData.logoFile) {
        fd.append('logo', formData.logoFile, formData.logoFile.name);
      }
      // Map frontend fields to backend parameter names
      fd.append('hospital_name', formData.hospitalName || '');
      fd.append('contact_per_name', formData.contactPersonName || '');
      fd.append('mobile', formData.mobileNumber || '');
      fd.append('whatsapp', formData.whatsappNumber || '');
      fd.append('telephone', formData.telephone || '');
      fd.append('email', formData.emailId || '');
      fd.append('address', formData.address || '');
      fd.append('city', formData.city || '');
      fd.append('state', formData.state || '');
      fd.append('pincode', formData.pincode || '');
      fd.append('terms', formData.termsAndConditions || '');

      // Include token in query as a fallback as well
      const apiUrl = `https://newerp.awasthivitality.com/api/company/settings?token=${encodeURIComponent(token)}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          // Send token via both common mechanisms used by backend
          'Authorization': `Bearer ${token}`,
          'X-Session-Token': token,
        },
        body: fd,
      });

      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response: ${ct} ${text?.slice(0, 120)}`);
      }

      const data = await response.json();

      if (data.success) {
        // Refresh profile to pick up latest logo_url and fields
        try {
          // Try both bases to ensure latest logo_url is read
          const bases = [
            'https://newerp.awasthivitality.com/api/company/showsettings',
            'http://13.202.42.222/apiawasthi/public/api/company/showsettings',
          ];
          let latest = null;
          for (const baseUrl of bases) {
            const urlShow = `${baseUrl}?token=${encodeURIComponent(token)}&_ts=${Date.now()}`;
            const ref = await fetch(urlShow, { headers: { 'Accept': 'application/json' } });
            try {
              const tmp = await ref.json();
              if (ref.ok && tmp.success) { latest = tmp; break; }
            } catch {}
          }
          if (latest && latest.success) {
            const mappedData = {
              profile_id: latest.data.profile_id,
              hospitalName: latest.data.hospital_name,
              profilePicture: latest.data.logo_url ? `${latest.data.logo_url}?_ts=${Date.now()}` : '',
              contactPersonName: latest.data.contact_per_name,
              mobileNumber: latest.data.mobile,
              whatsappNumber: latest.data.whatsapp,
              telephone: latest.data.telephone,
              emailId: latest.data.email,
              address: latest.data.address,
              city: latest.data.city,
              state: latest.data.state,
              pincode: latest.data.pincode,
              termsAndConditions: latest.data.terms,
            };
            setProfileData(mappedData);
          }
        } catch {}

        setStatusMessage({ text: 'Profile updated successfully!', type: 'success' });
        setIsEditing(false);
      } else {
        setStatusMessage({ text: data.message || data.msg || 'Failed to update profile.', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setStatusMessage({ text: 'Error updating profile.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({});
    setStatusMessage({ text: '', type: '' });
  };

  if (loadingData || permissionLoading) return <p>Loading profile...</p>;
  if (!profileData) return <p>Profile data not available.</p>;

  const InfoCard = ({ icon, label, value }) => (
    <div className="info-card">
      <div className="info-icon">{icon}</div>
      <div>
        <p className="info-label">{label}</p>
        <p className="info-value">{value}</p>
      </div>
    </div>
  );

  const FormInput = ({ label, name, value, onChange }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input
        type="text"
        name={name}
        value={value ?? ''}
        onChange={onChange}
        className="form-input"
      />
    </div>
  );

  const FormTextarea = ({ label, name, value, onChange }) => (
    <div className="form-group full-width">
      <label className="form-label">{label}</label>
      <textarea
        name={name}
        value={value ?? ''}
        onChange={onChange}
        rows="4"
        className="form-textarea"
      />
    </div>
  );

  const ProfileView = () => (
    <div className="profile-content-section">
      <div className="info-grid">
        <InfoCard icon={<User size={20} />} label="Contact Person" value={profileData.contactPersonName} />
        <InfoCard icon={<Phone size={20} />} label="Mobile" value={profileData.mobileNumber} />
        <InfoCard icon={<Phone size={20} />} label="Whatsapp" value={profileData.whatsappNumber} />
        <InfoCard icon={<Phone size={20} />} label="Telephone" value={profileData.telephone} />
        <InfoCard icon={<Mail size={20} />} label="Email" value={profileData.emailId} />
      </div>

      <div className="info-section">
        <h3 className="info-section-header">
          <MapPin size={20} className="section-icon" /> Address
        </h3>
        <p className="section-text">{profileData.address}</p>
        <div className="info-grid mt-4">
          <InfoCard icon={<MapPin size={20} />} label="City" value={profileData.city} />
          <InfoCard icon={<Building size={20} />} label="State" value={profileData.state} />
          <InfoCard icon={<Hash size={20} />} label="Pincode" value={profileData.pincode} />
        </div>
      </div>

      <div className="info-section">
        <h3 className="info-section-header">
          <FileText size={20} className="section-icon" /> Terms & Conditions
        </h3>
        <p className="section-text">{profileData.termsAndConditions}</p>
      </div>
    </div>
  );

  const ProfileEditForm = () => (
    <div className="profile-content-section">
      <div className="form-grid">
        <FormInput label="Hospital Name" name="hospitalName" value={formData.hospitalName || ''} onChange={handleInputChange} />
        <FormInput label="Contact Person Name" name="contactPersonName" value={formData.contactPersonName || ''} onChange={handleInputChange} />
        <FormInput label="Mobile Number" name="mobileNumber" value={formData.mobileNumber || ''} onChange={handleInputChange} />
        <FormInput label="Whatsapp Number" name="whatsappNumber" value={formData.whatsappNumber || ''} onChange={handleInputChange} />
        <FormInput label="Telephone" name="telephone" value={formData.telephone || ''} onChange={handleInputChange} />
        <FormInput label="Email ID" name="emailId" value={formData.emailId || ''} onChange={handleInputChange} />
      </div>

      <FormTextarea label="Address" name="address" value={formData.address || ''} onChange={handleInputChange} />

      <div className="form-grid">
        <FormInput label="City" name="city" value={formData.city || ''} onChange={handleInputChange} />
        <FormInput label="State" name="state" value={formData.state || ''} onChange={handleInputChange} />
        <FormInput label="Pincode" name="pincode" value={formData.pincode || ''} onChange={handleInputChange} />
      </div>

      <FormTextarea label="Terms & Conditions" name="termsAndConditions" value={formData.termsAndConditions || ''} onChange={handleInputChange} />
    </div>
  );

  return (
    <div className="app-container">
      <div className="profile-container">

        <div className="profile-header">
          <div className="header-content">
            <div className="profile-pic-wrapper">
              <img
                src={formData.profilePicture || profileData.profilePicture || 'img/small.png'}
                alt="Profile"
                className="profile-pic"
              />
              {isEditing && (
                <label className="change-logo-btn">
                  Change Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const previewUrl = URL.createObjectURL(file);
                        setFormData((prev) => ({ ...prev, profilePicture: previewUrl, logoFile: file }));
                      }
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div className="header-info">
              <h1 className="header-title">{formData.hospitalName || profileData.hospitalName}</h1>
              <p className="header-subtitle">{formData.contactPersonName || profileData.contactPersonName}</p>
            </div>
          </div>

          <div className="actions">
            {!isEditing && hasEditPermission && (
              <button className="btn edit-btn" onClick={() => setIsEditing(true)}>
                <Edit2 size={18} /> Edit Profile
              </button>
            )}

            {!isEditing && !hasEditPermission && (
              <div className="permission-denied">
                <Lock size={18} /> View Only - No Edit Permission
              </div>
            )}

            {isEditing && (
              <>
                <button className="btn save-btn" onClick={handleSave} disabled={isLoading}>
                  <Save size={18} /> {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button className="btn cancel-btn" onClick={handleCancel}>
                  <X size={18} /> Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {statusMessage.text && (
          <div className={`status-message ${statusMessage.type === 'success' ? 'status-success' : 'status-error'}`}>
            {statusMessage.text}
          </div>
        )}

        {isEditing ? <ProfileEditForm /> : <ProfileView />}
      </div>

      <style>{`
        :root {
          --primary-color: #4f46e5;
          --secondary-color: #8b5cf6;
          --success-color: #22c55e;
          --cancel-color: #6b7280;
          --bg-color: #f3f4f6;
          --text-color-primary: #1f2937;
          --text-color-secondary: #6b7280;
          --card-bg: #ffffff;
          --border-color: #e5e7eb;
          --input-bg: #f9fafb;
          --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; background-color: var(--bg-color); }
        .app-container { display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 1rem; }
        .profile-container { max-width: 100%; width: 100%; display: flex; flex-direction: column; gap: 1.5rem; background-color: var(--bg-color); }
        .profile-header { background: linear-gradient(135deg, #2fb2e7, #f0e9ff); color: white; padding: 1.5rem; border-radius: 1.5rem; box-shadow: var(--shadow-xl); display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1.5rem; }
        .header-content { display: flex; align-items: center; gap: 1rem; }
        .profile-pic { width: 6rem; height: 6rem; border-radius: 9999px; background: white; border: 4px solid white; object-fit: cover; box-shadow: var(--shadow-md); }
        .header-info { display: flex; flex-direction: column; }
        .header-title { font-size: 1.5rem; font-weight: 700; margin: 0; letter-spacing: -0.025em; }
        .header-subtitle { font-size: 1.125rem; font-weight: 500; opacity: 0.9; margin: 0; }
        .actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }
        .btn { padding: 0.75rem 1.25rem; border-radius: 0.5rem; border: none; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; font-weight: 600; transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease; }
        .permission-denied { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; border-radius: 0.5rem; background-color: #fef3c7; color: #92400e; font-weight: 600; border: 1px solid #fbbf24; }
        .btn:hover { transform: translateY(-2px); box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); }
        .edit-btn { background-color: white; color: var(--primary-color); }
        .save-btn { background-color: var(--success-color); color: white; }
        .cancel-btn { background-color: var(--cancel-color); color: white; }
        .status-message { padding: 1rem; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 500; }
        .status-success { background-color: #dcfce7; color: #166534; }
        .status-error { background-color: #fee2e2; color: #b91c1c; }
        .profile-content-section { display: flex; flex-direction: column; gap: 1.5rem; }
        .info-grid, .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; }
        .info-section { background-color: var(--card-bg); padding: 1.5rem; border-radius: 1.5rem; box-shadow: var(--shadow-md); display: flex; flex-direction: column; gap: 1rem; }
        .info-section-header { display: flex; align-items: center; gap: 0.5rem; border-bottom: 1px solid var(--border-color); padding-bottom: 0.75rem; margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--text-color-primary); }
        .section-icon { color: #2fb2e7; }
        .section-text { color: var(--text-color-secondary); line-height: 1.625; margin: 0; }
        .info-card { background-color: var(--card-bg); padding: 1rem; border-radius: 1rem; box-shadow: var(--shadow-sm); display: flex; align-items: center; gap: 0.75rem; transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .info-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
        .info-icon { background-color: #2fb2e7; color: white; border-radius: 9999px; padding: 0.5rem; display: flex; align-items: center; justify-content: center; }
        .info-label { font-size: 0.75rem; font-weight: 600; color: var(--text-color-secondary); text-transform: uppercase; letter-spacing: 0.05em; margin: 0; }
        .info-value { font-size: 1rem; font-weight: 500; color: var(--text-color-primary); margin: 0; }
        .form-group { display: flex; flex-direction: column; gap: 0.3rem; }
        .form-group.full-width { grid-column: 1 / -1; }
        .form-label { font-size: 0.875rem; font-weight: 500; color: var(--text-color-primary); }
        .form-input, .form-textarea { padding: 0.75rem; border-radius: 0.5rem; border: 1px solid var(--border-color); background-color: var(--input-bg); font-size: 1rem; width: 100%; transition: border 0.2s ease, box-shadow 0.2s ease; }
        .form-input:focus, .form-textarea:focus { border-color: var(--primary-color); outline: none; box-shadow: 0 0 0 3px rgba(79,70,229,0.2); }
        .form-textarea { resize: vertical; }
        .change-logo-btn { position: absolute; bottom: 0; right: 0; background: var(--primary-color); color: white; padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-size: 0.75rem; cursor: pointer; }
        .profile-pic-wrapper { position: relative; }
        .hidden { display: none; }
        @media (max-width: 768px) {
          .profile-header { flex-direction: column; align-items: flex-start; }
          .actions { margin-left: 0; }
          .header-title { font-size: 1.25rem; }
          .header-subtitle { font-size: 1rem; }
          .profile-pic { width: 5rem; height: 5rem; }
          .profile-container { margin-left: 0; padding: 0 1rem; }
        }
      `}</style>
    </div>
  );
};

export default Profile;